import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { Alert, Button, CircularProgress, Typography } from '@mui/material';
import { Container, Stack } from '@mui/system';

import { AppPath } from '@/shared/constants/path';
import useAuth from '@/shared/contexts/auth/hooks/useAuth';

import Form from './components/Form';
import LoginFormValues from './types/loginFormValues.type';

const Login: FC = () => {
  const { login } = useAuth();
  const { t } = useTranslation(['login', 'forms', 'common']);

  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as { from: Location } | undefined;

  const [formValues, setFormValues] = useState<LoginFormValues>({
    accountId: '',
    email: '',
    password: '',
    rememberMe: false,
  });

  const [formValuesErrors, setFormValuesErrors] = useState<
    Partial<LoginFormValues>
  >({});

  const [errorAPI, setErrorAPI] = useState<string | null>(null);
  const [isFormReady, setIsFormReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const accountId = localStorage.getItem('accountId');

    if (accountId) {
      setFormValues({ ...formValues, accountId: accountId, rememberMe: true });
      setIsFormReady(true);
    }

    setIsFormReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (formValues.rememberMe) {
      localStorage.setItem('accountId', formValues.accountId);
    } else {
      localStorage.removeItem('accountId');
    }
  }, [formValues.rememberMe, formValues.accountId]);

  const handleSubmit = async () => {
    if (isLoading) return;

    setIsLoading(true);
    const loginResponse = await login(formValues);

    if (loginResponse.success) {
      setIsLoading(false);
      navigate(state?.from || AppPath.Customers);
    } else {
      setIsLoading(false);
      setErrorAPI(loginResponse.msg);
    }
  };

  return (
    <Container
      maxWidth='sm'
      sx={{
        height: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Stack
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            void handleSubmit();
          }
        }}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
        }}
      >
        <Stack>
          <Typography variant='h5'>{t('login:login')}</Typography>
          <Typography variant='body2'>
            {t('login:getStartedFree')} - DEV ONLY
          </Typography>
        </Stack>

        <Form
          key={isFormReady.toString()}
          formValues={formValues}
          setFormValues={setFormValues}
          formValuesErrors={formValuesErrors}
          setFormValuesErrors={(errors: Partial<LoginFormValues>) => {
            setFormValuesErrors({ ...formValuesErrors, ...errors });
          }}
        />

        <Button
          variant='contained'
          color='primary'
          sx={{ p: 2, gap: 2 }}
          onClick={handleSubmit}
          disabled={
            Object.values(formValuesErrors).some((error) => !!error) ||
            !formValues.accountId ||
            !formValues.email ||
            !formValues.password
          }
        >
          {isLoading && <CircularProgress color='inherit' size={16} />}
          {t('login:login')}
        </Button>

        {errorAPI && <Alert severity='error'>{errorAPI}</Alert>}
      </Stack>
    </Container>
  );
};

export default Login;
